import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiOutlineDuplicate,
  HiOutlineCheck,
  HiOutlineUserAdd,
  HiOutlineShare,
  HiOutlineUserGroup,
  HiOutlineCash,
  HiOutlineChevronDown,
  HiOutlineQuestionMarkCircle,
} from 'react-icons/hi';
import { TbWallet } from 'react-icons/tb';
import { useWallet } from '../context/WalletContext';
import { useToast } from '../context/ToastContext';
import { shortAddress } from '../services/wallet';
import { InviteSkeleton } from '../components/Skeleton';
import PageHeader from '../components/PageHeader';

const STEPS = [
  {
    Icon: HiOutlineShare,
    title: 'Share your link',
    text: 'Copy your personal invite URL and send it to friends via chat, social, or email.',
  },
  {
    Icon: HiOutlineUserGroup,
    title: 'They register under you',
    text: 'When someone opens your link and connects a new wallet, your address is saved as their upline.',
  },
  {
    Icon: HiOutlineCash,
    title: 'Earn from activity',
    text: 'When your direct invite buys a plan, you can receive a direct commission (see Admin settings). Track your team on the Team page.',
  },
];

const FAQS = [
  {
    q: 'What is an invite link?',
    a: 'Your invite link is a unique dashboard URL that includes your wallet address. New members who open it will register with you as their upline.',
  },
  {
    q: 'Do I need to invest before inviting?',
    a: 'You can share your link as soon as your wallet is registered. Building a team works best when you are active on plans yourself.',
  },
  {
    q: 'How does upline commission work?',
    a: 'When someone you directly invited purchases a plan, a percentage of that purchase can be credited to your account (default 5%, managed by admin). Income still respects the platform earn cap.',
  },
  {
    q: 'What if someone already has an account?',
    a: 'Invite links only apply when registering a new wallet. Existing members keep their original upline.',
  },
  {
    q: 'Where can I see my downline?',
    a: 'Open the Team page in the sidebar. It lists every downline member with their level (Level 1 = direct invites).',
  },
  {
    q: 'Can I change my invite link?',
    a: 'The link is tied to your connected wallet address, so it stays the same for that wallet. If you use another wallet, that wallet gets its own invite link after registration.',
  },
];

function FaqItem({ item, open, onToggle }) {
  return (
    <div className={`faq-item${open ? ' is-open' : ''}`}>
      <button type="button" className="faq-item__q" onClick={onToggle} aria-expanded={open}>
        <span>{item.q}</span>
        <HiOutlineChevronDown className="faq-item__chevron" />
      </button>
      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            className="faq-item__a"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <p>{item.a}</p>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export default function InvitePage() {
  const { user, address, openConnect, connectOpening, loading, config } = useWallet();
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const wallet = (user?.wallet_address || address || '').toLowerCase();
  const commission = config?.direct_upline_commission_percent || '5';

  const inviteLink = useMemo(() => {
    if (!wallet) return '';
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/dashboard/${wallet}`;
  }, [wallet]);

  async function copyLink() {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      toast.success('Invite link copied');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      const el = document.createElement('textarea');
      el.value = inviteLink;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      toast.success('Invite link copied');
      setTimeout(() => setCopied(false), 1800);
    }
  }

  if (loading) {
    return <InviteSkeleton />;
  }

  if (!user) {
    return (
      <>
        <PageHeader title="Invite" subtitle="Share your referral link" />
        <motion.div
          className="panel panel--glow"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="eyebrow">Invite</div>
          <h2>Connect to get your invite link</h2>
          <p className="muted">
            Your personal invite link uses your wallet address so new members can join under you.
          </p>
          <div className="stack">
            <button
              className={`btn btn--primary${connectOpening ? ' is-busy' : ''}`}
              type="button"
              onClick={() => openConnect()}
              disabled={connectOpening}
              aria-busy={connectOpening}
            >
              {connectOpening ? <span className="btn__spin" aria-hidden /> : <TbWallet />}
              {connectOpening ? 'Connecting…' : 'Connect Wallet'}
            </button>
          </div>
        </motion.div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Invite" subtitle="Share your referral link" />
      <motion.section
        className="panel panel--glow panel--tint-violet"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="eyebrow">
          <HiOutlineUserAdd style={{ marginRight: 4 }} />
          Invite
        </div>
        <h2 style={{ marginBottom: '0.35rem' }}>Share your invite link</h2>
        <p className="muted" style={{ marginTop: 0, maxWidth: '38rem', lineHeight: 1.55 }}>
          Anyone who opens this link and registers will use{' '}
          <span className="mono">{shortAddress(wallet)}</span> as their upline. Direct plan purchases
          can credit you about <strong>{commission}%</strong> commission (platform settings).
        </p>

        <div className="invite-box">
          <code className="invite-box__link mono">{inviteLink}</code>
          <motion.button
            className="btn btn--primary"
            type="button"
            onClick={copyLink}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {copied ? <HiOutlineCheck /> : <HiOutlineDuplicate />}
            {copied ? 'Copied' : 'Copy link'}
          </motion.button>
        </div>

        <div className="stack">
          <Link className="btn btn--ghost" to="/team" style={{ textDecoration: 'none' }}>
            <HiOutlineUserGroup /> View my team
          </Link>
        </div>
      </motion.section>

      <div className="feature-grid" style={{ marginTop: '1rem' }}>
        {STEPS.map(({ Icon, title, text }, i) => (
          <motion.article
            key={title}
            className="feature"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 * i }}
          >
            <h3>
              <Icon style={{ marginRight: 8, verticalAlign: 'middle', color: 'var(--cyan)' }} />
              {title}
            </h3>
            <p>{text}</p>
          </motion.article>
        ))}
      </div>

      <div className="panel-grid">
        <motion.section
          className="panel"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="eyebrow">
            <HiOutlineQuestionMarkCircle style={{ marginRight: 4 }} />
            FAQ
          </div>
          <h3 style={{ marginTop: 0 }}>Invite & referral questions</h3>
          <div className="faq-list">
            {FAQS.map((item, i) => (
              <FaqItem
                key={item.q}
                item={item}
                open={openFaq === i}
                onToggle={() => setOpenFaq((prev) => (prev === i ? -1 : i))}
              />
            ))}
          </div>
        </motion.section>

        <motion.section
          className="panel"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.14 }}
        >
          <h3 style={{ marginTop: 0 }}>Tips for better invites</h3>
          <ul className="invite-tips">
            <li>Share the link after explaining how to open it in MetaMask or Trust Wallet browser.</li>
            <li>Ask new members to complete registration with your link before buying a plan.</li>
            <li>Check the Team page regularly to see who joined at each level.</li>
            <li>Need help? Visit Support for Telegram and email contacts.</li>
          </ul>
        </motion.section>
      </div>
    </>
  );
}
